import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, User } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { isDevMode } from '../auth/DevModeAuth';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Export the appropriate component based on mode
export const UserMenu = isDevMode ? DevUserMenu : ClerkUserMenu;

/**
 * Dev mode user menu - uses mock data
 */
// eslint-disable-next-line react-refresh/only-export-components
function DevUserMenu() {
  const navigate = useNavigate();

  const displayName = 'Dev User';
  const email = 'dev@localhost';
  const initials = 'DU';

  const handleSignOut = () => {
    navigate('/login');
  };

  return (
    <UserMenuUI
      displayName={displayName}
      email={email}
      initials={initials}
      imageUrl={null}
      isDevMode={true}
      onSignOut={handleSignOut}
      onNavigate={navigate}
    />
  );
}

/**
 * Production user menu - uses Clerk
 */
// eslint-disable-next-line react-refresh/only-export-components
function ClerkUserMenu() {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();

  if (!isLoaded) {
    return (
      <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
    );
  }

  if (!user) {
    return null;
  }

  const displayName = user.fullName || user.primaryEmailAddress?.emailAddress || 'User';
  const email = user.primaryEmailAddress?.emailAddress;
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSignOut = () => {
    signOut(() => navigate('/login'));
  };

  return (
    <UserMenuUI
      displayName={displayName}
      email={email}
      initials={initials}
      imageUrl={user.imageUrl}
      isDevMode={false}
      onSignOut={handleSignOut}
      onNavigate={navigate}
    />
  );
}

interface UserMenuUIProps {
  displayName: string;
  email?: string | null;
  initials: string;
  imageUrl?: string | null;
  isDevMode: boolean;
  onSignOut: () => void;
  onNavigate: (path: string) => void;
}

/**
 * Shared UI component for user menu
 */
// eslint-disable-next-line react-refresh/only-export-components
function UserMenuUI({ displayName, email, initials, imageUrl, isDevMode: isDevModeUI, onSignOut, onNavigate }: UserMenuUIProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            {imageUrl && (
              <AvatarImage src={imageUrl} alt={displayName} />
            )}
            <AvatarFallback className="bg-primary text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            {email && (
              <p className="text-xs leading-none text-muted-foreground">
                {email}
              </p>
            )}
            {isDevModeUI && (
              <p className="text-xs leading-none text-yellow-500 mt-1">
                Dev Mode
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onNavigate('/settings')}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate('/profile')}>
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
